import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, CupSoda, PackageCheck, ShieldCheck, Snowflake, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Modal from '../components/Modal';
import { activateAurasItem, getAurasBalance, purchaseAurasItem } from '../api/dataService';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';

const SHOP_ITEMS = {
  icebath: {
    cost: 1500,
    icon: Snowflake,
  },
  preworkout: {
    cost: 2500,
    icon: CupSoda,
  },
  cheatmeal: {
    cost: 1000,
    icon: ShieldCheck,
  },
};

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0 },
};

const isEffectActive = (effect) => {
  if (!effect) return false;
  return new Date(effect.expiresAt).getTime() > Date.now();
};

const remainingLabel = (effect, t) => {
  if (!effect || !effect.expiresAt) return '';
  const ms = new Date(effect.expiresAt).getTime() - Date.now();
  if (ms <= 0) return t('shop.expired', 'Expired');
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const getModalCopy = ({ actionType, item, itemActive, t }) => {
  if (actionType === 'buy') {
    return {
      title: t('shop.buyTitle', 'Buy {itemName}', { itemName: item.name }),
      body: t('shop.buyBody', 'Add 1 {itemName} to your inventory. You can activate it later.', { itemName: item.name }),
      confirmLabel: t('shop.buy', 'Buy'),
      metricLabel: t('shop.cost', 'Cost'),
      metricValue: `${item.cost.toLocaleString()} Auras`,
      tone: 'cyan',
    };
  }

  if (itemActive) {
    return {
      title: t('shop.extendTitle', 'Extend {itemName}', { itemName: item.name }),
      body: t('shop.extendBody', 'Extend {itemName} by 24 hours and consume 1 owned item.', { itemName: item.name }),
      confirmLabel: t('shop.extend', 'Extend +24h'),
      metricLabel: t('shop.consumes', 'Consumes'),
      metricValue: t('shop.oneItem', '1 item'),
      tone: 'emerald',
    };
  }

  return {
    title: t('shop.activateTitle', 'Activate {itemName}', { itemName: item.name }),
    body: t('shop.activateBody', 'Activate {itemName} for 24 hours and consume 1 owned item.', { itemName: item.name }),
    confirmLabel: t('shop.activate', 'Activate'),
    metricLabel: t('shop.consumes', 'Consumes'),
    metricValue: t('shop.oneItem', '1 item'),
    tone: 'emerald',
  };
};

const ShopPage = () => {
  const [balance, setBalance] = useState(0);
  const [effects, setEffects] = useState([]);
  const [inventory, setInventory] = useState({});
  const [loading, setLoading] = useState(true);
  const [pendingAction, setPendingAction] = useState(null);
  const { addToast } = useToast();
  const { setUser } = useAuth();
  const { t } = useLocale();

  const getItemCopy = (key) => ({
    name: t(`shop.items.${key}.name`, key),
    subtitle: t(`shop.items.${key}.subtitle`, ''),
    description: t(`shop.items.${key}.description`, ''),
  });

  const activeMap = useMemo(() => {
    const map = {};
    (effects || []).forEach((eff) => {
      if (isEffectActive(eff)) {
        map[eff.type] = eff;
      }
    });
    return map;
  }, [effects]);

  const activeBoosts = useMemo(() => (
    Object.entries(activeMap)
      .map(([key, effect]) => ({
        key,
        effect,
        item: SHOP_ITEMS[key],
      }))
      .filter((entry) => entry.item)
  ), [activeMap]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getAurasBalance();
        setBalance(res.data.balance || 0);
        setEffects(res.data.effects || []);
        setInventory(res.data.inventory || {});
      } catch (err) {
        console.error('[Shop] Failed to load Auras balance', err);
        addToast(t('shop.loadFailed', 'Failed to load Auras data'), 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [addToast, t]);

  const syncAurasState = ({ nextBalance, nextEffects, nextInventory }) => {
    setBalance(nextBalance);
    setEffects(nextEffects);
    setInventory(nextInventory);
    setUser((prev) => ({
      ...(prev || {}),
      aurasBalance: nextBalance,
      aurasEffects: nextEffects,
      aurasInventory: nextInventory,
    }));
  };

  const handleConfirmPurchase = async (item) => {
    if (!item) return;
    try {
      const res = await purchaseAurasItem(item);
      const nextBalance = res.data.aurasBalance ?? balance;
      const nextEffects = res.data.aurasEffects || effects;
      const nextInventory = res.data.aurasInventory || inventory;
      syncAurasState({ nextBalance, nextEffects, nextInventory });
      addToast(t('shop.purchasedToast', '{itemName} purchased', { itemName: getItemCopy(item).name }), 'success');
    } catch (err) {
      const message = err.response?.data?.message || t('shop.purchaseFailed', 'Purchase failed');
      addToast(message, 'error');
    }
  };

  const handleConfirmActivate = async (item) => {
    if (!item) return;
    try {
      const res = await activateAurasItem(item);
      const nextBalance = res.data.aurasBalance ?? balance;
      const nextEffects = res.data.aurasEffects || effects;
      const nextInventory = res.data.aurasInventory || inventory;
      syncAurasState({ nextBalance, nextEffects, nextInventory });

      const action = res.data.action;
      if (action === 'extended') {
        addToast(t('shop.extendedToast', '{itemName} extended +24h', { itemName: getItemCopy(item).name }), 'success');
      } else {
        addToast(t('shop.activatedToast', '{itemName} activated', { itemName: getItemCopy(item).name }), 'success');
      }
    } catch (err) {
      const message = err.response?.data?.message || t('shop.activationFailed', 'Activation failed');
      addToast(message, 'error');
    }
  };

  const renderPendingAction = () => {
    if (!pendingAction?.item) {
      return null;
    }

    const pendingItem = pendingAction.item;
    const actionType = pendingAction.type;
    const item = SHOP_ITEMS[pendingItem];
    const itemCopy = getItemCopy(pendingItem);
    const Icon = item.icon;
    const itemActive = activeMap[pendingItem];
    const owned = inventory?.[pendingItem] || 0;
    const modalCopy = getModalCopy({ actionType, item: { ...item, ...itemCopy }, itemActive, t });
    const confirmClassName = modalCopy.tone === 'emerald'
      ? 'bg-emerald-500 text-slate-950 hover:bg-emerald-400 focus-visible:ring-emerald-300'
      : 'bg-cyan-300 text-slate-950 hover:bg-cyan-200 focus-visible:ring-cyan-200';

    const confirmHandler = async () => {
      if (actionType === 'activate') {
        await handleConfirmActivate(pendingItem);
      } else {
        await handleConfirmPurchase(pendingItem);
      }
      setPendingAction(null);
    };

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        className="space-y-4"
      >
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-300/25 bg-cyan-300/10 shadow-inner">
            <Icon className="h-7 w-7 text-cyan-300" />
          </div>
          <h2 className="text-2xl font-black tracking-tight text-white">{modalCopy.title}</h2>
          <p className="mt-2 text-sm leading-6 text-slate-300">{modalCopy.body}</p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-slate-950/55 p-4 shadow-inner">
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm font-semibold text-slate-300">{modalCopy.metricLabel}</span>
            <span className={`text-base font-black tabular-nums ${actionType === 'buy' ? 'text-yellow-300' : 'text-white'}`}>
              {modalCopy.metricValue}
            </span>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-slate-950/55 p-4 shadow-inner">
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm font-semibold text-slate-300">{t('shop.ownedLabel', 'Owned')}</span>
            <span className="text-base font-black text-white tabular-nums">{owned}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 pt-2">
          <button
            onClick={() => setPendingAction(null)}
            className="rounded-2xl border border-white/10 px-4 py-3 text-sm font-bold text-slate-300 transition hover:border-white/25 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300"
          >
            {t('shop.cancel', 'Cancel')}
          </button>
          <button
            onClick={confirmHandler}
            className={`rounded-2xl px-4 py-3 text-sm font-black transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 ${confirmClassName}`}
          >
            {modalCopy.confirmLabel}
          </button>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen pb-24 font-sans text-gray-300">
      <motion.main
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="mx-auto flex w-full max-w-md flex-col gap-4 px-4 pt-4"
      >
        <motion.section
          variants={itemVariants}
          className="relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-slate-900/40 p-5 shadow-xl backdrop-blur-2xl"
        >
          <div className="absolute left-0 top-0 h-32 w-32 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cyan-500/20 blur-3xl" />
          <div className="relative">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.22em] text-cyan-300">{t('shop.balanceLabel', 'Auras')}</p>
                <h1 className="mt-2 text-4xl font-black tracking-tight text-white">
                  {balance.toLocaleString()}
                </h1>
                <p className="mt-1 text-sm font-semibold text-slate-300">{t('shop.available', 'available')}</p>
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-300/30 bg-cyan-300/10 shadow-inner">
                <Sparkles className="h-7 w-7 text-cyan-200" />
              </div>
            </div>

            <p className="mt-4 text-sm leading-6 text-slate-300">
              {t('shop.intro', 'Use Auras to protect streaks, boost XP, and recover without losing momentum.')}
            </p>

            <div className="mt-5 rounded-2xl border border-white/10 bg-slate-950/45 p-3 shadow-inner">
              <div className="mb-3 flex items-center justify-between gap-3">
                <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">{t('shop.activeBoosts', 'Active boosts')}</span>
                <span className="text-xs font-bold text-cyan-200 tabular-nums">{t('shop.activeCount', '{count} active', { count: activeBoosts.length })}</span>
              </div>
              {activeBoosts.length ? (
                <div className="flex flex-wrap gap-2">
                  {activeBoosts.map(({ key, item, effect }) => {
                    const Icon = item.icon;
                    const itemCopy = getItemCopy(key);
                    return (
                      <span
                        key={key}
                        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300/25 bg-emerald-400/10 px-3 py-1.5 text-xs font-bold text-emerald-100"
                      >
                        <Icon className="h-3.5 w-3.5 text-emerald-300" />
                        {t('shop.activeItemWithTime', '{itemName} · {time}', {
                          itemName: itemCopy.name,
                          time: remainingLabel(effect, t),
                        })}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm font-semibold text-slate-500">{t('shop.noActiveBoosts', 'No active boosts')}</p>
              )}
            </div>
          </div>
        </motion.section>

        <section className="space-y-3">
          <div className="flex items-end justify-between gap-4 px-1">
            <div>
              <h2 className="text-xl font-black text-white">{t('shop.inventoryTitle', 'Boost inventory')}</h2>
              <p className="mt-1 text-sm text-slate-400">{t('shop.inventorySubtitle', 'Buy now, activate when the timing matters.')}</p>
            </div>
          </div>

          {Object.entries(SHOP_ITEMS).map(([key, item]) => {
            const Icon = item.icon;
            const itemCopy = getItemCopy(key);
            const active = activeMap[key];
            const canAfford = balance >= item.cost;
            const owned = inventory?.[key] || 0;
            const canActivate = owned > 0;

            return (
              <motion.article
                key={key}
                variants={itemVariants}
                className={`relative overflow-hidden rounded-[1.5rem] border p-0.5 shadow-xl backdrop-blur-md transition-colors duration-200 ${
                  active
                    ? 'border-emerald-300/25 bg-emerald-400/10'
                    : 'border-white/10 bg-slate-900/40'
                }`}
              >
                <div className="relative rounded-[1.35rem] bg-slate-950/50 p-4">
                  <div className="flex items-start gap-4">
                    <div className="relative flex-shrink-0">
                      <div className="absolute inset-0 rounded-2xl bg-cyan-400/20 blur-lg" />
                      <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-slate-900/80 shadow-inner">
                        <Icon className="h-7 w-7 text-cyan-300" />
                      </div>
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h3 className="text-lg font-black leading-tight text-white">{itemCopy.name}</h3>
                          <p className="mt-1 text-xs font-black uppercase tracking-[0.16em] text-slate-500">
                            {itemCopy.subtitle}
                          </p>
                        </div>
                        <span className="rounded-xl border border-yellow-300/20 bg-yellow-300/10 px-2.5 py-1 text-xs font-black text-yellow-300">
                          {item.cost.toLocaleString()} Auras
                        </span>
                      </div>

                      <p className="mt-3 text-sm leading-6 text-slate-300">{itemCopy.description}</p>

                      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-bold text-slate-300">
                          <PackageCheck className="h-3.5 w-3.5 text-cyan-300" />
                          {t('shop.owned', 'Owned: {count}', { count: owned })}
                        </span>
                        {active ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300/25 bg-emerald-400/10 px-3 py-1 text-xs font-bold text-emerald-100">
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" />
                            {t('shop.activeWithTime', 'Active · {time}', { time: remainingLabel(active, t) })}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      disabled={loading || !canAfford}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPendingAction({ type: 'buy', item: key });
                      }}
                      className={`rounded-2xl px-4 py-3 text-sm font-black transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 ${
                        canAfford
                          ? 'bg-cyan-300 text-slate-950 shadow-lg shadow-cyan-500/20 hover:bg-cyan-200'
                          : 'cursor-not-allowed bg-slate-800 text-slate-500'
                      }`}
                    >
                      {t('shop.buy', 'Buy')}
                    </button>

                    <button
                      type="button"
                      disabled={loading || !canActivate}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPendingAction({ type: 'activate', item: key });
                      }}
                      className={`rounded-2xl px-4 py-3 text-sm font-black transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 ${
                        canActivate
                          ? 'bg-emerald-500 text-slate-950 shadow-lg shadow-emerald-900/20 hover:bg-emerald-400'
                          : 'cursor-not-allowed bg-slate-800 text-slate-500'
                      }`}
                    >
                      {active ? t('shop.extend', 'Extend +24h') : t('shop.activate', 'Activate')}
                    </button>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </section>
      </motion.main>

      <Modal isOpen={Boolean(pendingAction)} onClose={() => setPendingAction(null)}>
        <AnimatePresence>{renderPendingAction()}</AnimatePresence>
      </Modal>
    </div>
  );
};

export default ShopPage;
